import { z } from 'zod'
import {
  GAME_TYPES,
  KEY_STATUSES,
  REQUEST_STATUSES,
  UNLOCK_TYPES,
} from './enums.js'

/**
 * Validation for everything the client sends. Server routes validate against
 * these via @hono/zod-validator; the web app imports the inferred types so the
 * two can never drift.
 */

const uuid = z.string().uuid()

/** Comma-separated query params (`?stores=steam,gog`) parsed into arrays. */
const csv = z
  .string()
  .transform((value) => value.split(',').map((part) => part.trim()).filter(Boolean))
  .pipe(z.array(z.string().min(1)).max(50))

export const CATALOG_SORTS = ['recent', 'released', 'score', 'title', 'copies'] as const
export type CatalogSort = (typeof CATALOG_SORTS)[number]

/**
 * What the catalog opens on. Best reviewed rather than newest: this is a
 * shelf of spare keys, and the useful first question is "is any of this any
 * good", not "what did the owner paste in most recently".
 *
 * Named here so the server, the client and the URL cannot disagree about
 * which value is the one that stays out of the address bar.
 */
export const DEFAULT_CATALOG_SORT: CatalogSort = 'score'

export const catalogQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  stores: csv.optional(),
  genres: csv.optional(),
  platforms: csv.optional(),
  /** Release year bounds, inclusive. */
  from: z.coerce.number().int().min(1970).max(2100).optional(),
  to: z.coerce.number().int().min(1970).max(2100).optional(),
  /** Minimum review score, as a percentage of the weighted score. */
  minScore: z.coerce.number().int().min(0).max(100).optional(),
  /** What a key unlocks: base, bundle, dlc, other. */
  unlock: csv.optional(),
  /** `expiring` = has a key expiring within 30 days. */
  filter: z.enum(['all', 'expiring']).default('all'),
  sort: z.enum(CATALOG_SORTS).default(DEFAULT_CATALOG_SORT),
  page: z.coerce.number().int().min(1).max(500).default(1),
  perPage: z.coerce.number().int().min(1).max(120).default(60),
})
export type CatalogQuery = z.input<typeof catalogQuerySchema>

export const createRequestSchema = z.object({
  gameId: uuid,
  /** A specific key, when picked from the game page rather than "any copy". */
  keyId: uuid.optional(),
  requesterName: z.string().trim().min(2).max(60),
  message: z.string().trim().max(500).optional(),
  /**
   * Honeypot. Real people never see this field, so anything in it is a bot.
   * Named plausibly on purpose — `honeypot` would be trivially skippable.
   *
   * Validation deliberately ACCEPTS a filled value rather than rejecting it.
   * A schema-level `.max(0)` would answer with "website: expected <= 0
   * characters", which tells a script precisely which field to stop filling
   * in. The route accepts the submission, answers 200, and quietly discards
   * it, so a bot gets no signal to tune against.
   */
  website: z.string().max(200).optional(),
})
export type CreateRequestInput = z.infer<typeof createRequestSchema>

export const loginSchema = z.object({
  password: z.string().min(1).max(200),
})

export const addKeysSchema = z
  .object({
    gameId: uuid,
    storeId: uuid,
    sourceId: uuid.optional(),
    /** Creates a source on the fly when the combobox had no match. */
    newSourceName: z.string().trim().min(2).max(120).optional(),
    /** Raw paste — one code per line. */
    codes: z.string().min(1).max(20_000),
    /** What these keys unlock. Applies to the whole batch. */
    unlockType: z.enum(UNLOCK_TYPES).default('base'),
    /** Optional specifics — "Digital Deluxe Edition", "The Eldritch Box". */
    edition: z.string().trim().max(140).optional(),
    expiresAt: z.coerce.date().optional(),
    note: z.string().trim().max(500).optional(),
  })
  .refine((value) => !(value.sourceId && value.newSourceName), {
    message: 'Pick an existing source or name a new one, not both',
    path: ['newSourceName'],
  })
export type AddKeysInput = z.infer<typeof addKeysSchema>

/** Editing a key. Every field optional — the form sends what changed. */
export const updateKeySchema = z.object({
  status: z.enum(KEY_STATUSES).optional(),
  storeId: uuid.optional(),
  sourceId: uuid.nullable().optional(),
  unlockType: z.enum(UNLOCK_TYPES).optional(),
  edition: z.string().trim().max(140).nullable().optional(),
  expiresAt: z.coerce.date().nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
  /**
   * The code itself, for fixing a typo in a hand-copied key. Changing it
   * re-derives the duplicate-detection form, so the same key still cannot end
   * up stored twice.
   */
  code: z.string().trim().min(1).max(200).optional(),
})
export type UpdateKeyInput = z.infer<typeof updateKeySchema>

export const createGameFromIgdbSchema = z.object({
  igdbId: z.number().int().positive(),
})

/**
 * A game no provider carries. Only the title is required — but everything the
 * edit page can set is accepted here too, so filing one properly does not mean
 * saving it and immediately going somewhere else to finish the job.
 */
export const manualGameSchema = z.object({
  title: z.string().trim().min(1).max(200),
  type: z.enum(GAME_TYPES).default('game'),
  summary: z.string().trim().max(4000).optional(),
  releaseDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .optional(),
  developer: z.string().trim().max(160).optional(),
  publisher: z.string().trim().max(160).optional(),
  capsuleUrl: z.string().url().max(500).optional(),
  steamAppId: z.number().int().positive().optional(),
  igdbId: z.number().int().positive().optional(),
  genreNames: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
  platformSlugs: z.array(z.string().min(1)).max(20).optional(),
})

/** Editing a game. Every field optional — the form sends what changed. */
export const updateGameSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  type: z.enum(GAME_TYPES).optional(),
  summary: z.string().trim().max(4000).nullable().optional(),
  releaseDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .nullable()
    .optional(),
  developer: z.string().trim().max(160).nullable().optional(),
  publisher: z.string().trim().max(160).nullable().optional(),
  steamAppId: z.number().int().positive().nullable().optional(),
  igdbId: z.number().int().positive().nullable().optional(),
  isHidden: z.boolean().optional(),
  genreNames: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
  platformSlugs: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
})

/**
 * The "Store pages" links on a game. Sent as the whole set, because the form
 * shows the whole set — what it sends is the intended final state.
 */
export const setStoreLinksSchema = z.object({
  links: z
    .array(
      z.object({
        storeSlug: z.string().trim().min(1).max(64),
        url: z.string().url().max(500),
      }),
    )
    .max(20),
})

/** Replacing one piece of artwork with an image from a URL. */
export const setArtworkSchema = z.object({
  url: z.string().url().max(1000),
})

export const IMAGE_SLOTS = ['capsule', 'hero', 'logo'] as const
export type ImageSlotName = (typeof IMAGE_SLOTS)[number]

export const IMAGE_SLOT_LABELS: Record<ImageSlotName, string> = {
  capsule: 'Cover (portrait)',
  hero: 'Banner (wide)',
  logo: 'Logo (transparent)',
}

const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Must be a hex colour like #1A9FFF')

export const upsertStoreSchema = z.object({
  name: z.string().trim().min(1).max(60),
  brandColor: hexColor.optional(),
  urlTemplate: z.string().url().max(300).nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
})

/**
 * A source is usually a storefront in its own right, so it carries the same
 * colour and ordering as a store rather than a category and a date.
 */
export const upsertSourceSchema = z.object({
  name: z.string().trim().min(2).max(120),
  brandColor: hexColor.optional(),
  url: z.string().url().max(500).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
})

/** Editing a source. Its slug stays put, so existing links keep working. */
export const updateSourceSchema = upsertSourceSchema.partial()

export const decideRequestSchema = z.object({
  /** Which specific key to hand over. Required when approving. */
  keyId: uuid.optional(),
  adminNote: z.string().trim().max(500).optional(),
})

export const requestQuerySchema = z.object({
  status: z.enum(REQUEST_STATUSES).optional(),
  page: z.coerce.number().int().min(1).default(1),
})

export const gameSearchQuerySchema = z.object({
  q: z.string().trim().min(1).max(120),
})
